import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useSelector } from "react-redux";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text } from "../elements";
import { formatAmountSuffix, getAcronym } from "../../utils/formatters";

const LINE_COLORS = {
  Income: "#2351af",
  Expense: "#c71414",
  Profit: "#18633a",
};

const ProjectTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload?.length) return null;
  const fullName = payload[0].payload.fullName || label;
  return (
    <Box
      sx={{
        background: colors.bg,
        borderRadius: "8px",
        padding: "11px 18px 12px",
        border: `1px solid ${colors.frame}`,
        boxShadow: "0px 12px 25px 0px rgb(0 0 0 / 12%)",
        maxWidth: 220,
      }}
    >
      <Heading
        as="h6"
        style={{
          color: colors.title,
          marginBottom: "8px",
          fontSize: "12px",
          fontWeight: "600",
          textTransform: "uppercase",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {fullName}
      </Heading>
      {payload.map((entry, i) => (
        <Box key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: "2px" }} />
          <Text
            as="span"
            style={{
              color: colors.text,
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            {entry.name}: {formatAmountSuffix(entry.value, true)}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

export function ProjectSummaryChart({ fromDate, toDate }) {
  const { theme } = useContext(ThemeContext);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const userDetails = useSelector((state) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const dark = { border: "#39496b", frame: "#2f3f61", title: "#f0f0f0", text: "#d1d1d1", bg: "#1b2b4d" };
  const light = { border: "#d1d1d1", frame: "#f0f0f0", title: "#403e57", text: "#5e5d72", bg: "#ffffff" };

  const colors = theme !== "light_mode" ? dark : light;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await AnalyticsService.getCompanyProjectSummary({
        page: 1,
        limit: 11,
        ...(companyId ? { companyId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      });
      const rows = data?.rows || [];
      setChartData(
        rows.map((project) => ({
          name: getAcronym(project.projectName),
          fullName: project.projectName,
          Income: parseFloat(project.totalIncome || 0),
          Expense: parseFloat(project.totalExpense || 0),
          Profit: parseFloat(project.profit || 0),
        })),
      );
    } catch (error) {
      console.error("🚀 ~ ProjectSummaryChart ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box
      className="mc-card"
      sx={{
        mt: 0,
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box className="mc-card-header" style={{ display: "flex", justifyContent: "center" }}>
        <Heading
          as="h2"
          className="mc-card-title"
          style={{ fontSize: "20px", fontWeight: 600, paddingBottom: "10px", color: colors.title }}
        >
          Project Summary
        </Heading>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : chartData.length > 0 ? (
        <Box
          sx={{
            height: 380,
            minHeight: 380,
            width: "100%",
            minWidth: 0,
            flex: 1,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 15, right: 15, left: 0, bottom: 5 }}>
              <CartesianGrid stroke={colors.border} strokeDasharray="1 3" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                fontSize="11px"
                fontWeight="500"
                tickMargin={10}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                fontSize="11px"
                fontWeight="500"
                tickFormatter={(v) => formatAmountSuffix(v)}
                width={80}
              />
              <RechartsTooltip
                content={<ProjectTooltip colors={colors} />}
                cursor={{ stroke: colors.border, strokeWidth: 1, strokeDasharray: "3 3" }}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: "12px", color: colors.text }} />

              {/* Smooth Monotone Line Series */}
              <Line
                type="monotone"
                dataKey="Income"
                stroke={LINE_COLORS.Income}
                strokeWidth={2.5}
                dot={{ fill: colors.bg, stroke: LINE_COLORS.Income, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: colors.bg, strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="Expense"
                stroke={LINE_COLORS.Expense}
                strokeWidth={2.5}
                dot={{ fill: colors.bg, stroke: LINE_COLORS.Expense, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: colors.bg, strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="Profit"
                stroke={LINE_COLORS.Profit}
                strokeWidth={2.5}
                dot={{ fill: colors.bg, stroke: LINE_COLORS.Profit, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: colors.bg, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text style={{ color: colors.text }}>No project data available</Text>
        </Box>
      )}
    </Box>
  );
}
